import { BoundSelectField, BoundSelectFieldProps, HasIdAndName, Optional, Value } from "@homebound/beam";
import { Observer } from "mobx-react";
import { ObjectState } from "src/utils/formState";
import { AiFieldProposalFormValue } from "./utils/aiFieldProposalForm";
import { AiProposalParse, parseAiString, pendingProposedValue, resolveAiProposalStatus } from "./utils/bindAiProposal";

/** Like Beam's `{ id, name }` overload of `BoundSelectField`, so callers can skip `getOptionValue`/`getOptionLabel`. */
export type BoundAiSelectFieldProps<O extends HasIdAndName<V>, V extends Value> = Optional<
  Omit<BoundSelectFieldProps<O, V>, "proposedValue">,
  "getOptionLabel" | "getOptionValue"
> & {
  aiProposal?: ObjectState<AiFieldProposalFormValue>;
  /**
   * Coerce GraphQL's string `fieldValue` into the select's value type.
   * Defaults to the raw string (enum codes). Pass a lookup when the proposal is a name and the field is an id.
   */
  parse?: AiProposalParse<V>;
};

export function BoundAiSelectField<O extends HasIdAndName<V>, V extends Value>(props: BoundAiSelectFieldProps<O, V>) {
  const { field, aiProposal, onSelect, parse = parseAiString as AiProposalParse<V>, ...others } = props;
  // Observer wrapper needed for `aiProposal.status` / `fieldValue` so the pending proposal stays in sync.
  return (
    <Observer>
      {() => (
        <BoundSelectField
          field={field}
          proposedValue={pendingProposedValue(aiProposal, parse)}
          onSelect={(value, opt) => {
            field.set(value);
            resolveAiProposalStatus(aiProposal, parse, value);
            onSelect?.(value, opt);
          }}
          {...others}
        />
      )}
    </Observer>
  );
}
